import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  acceptInvitationByToken,
  declineInvitationByToken,
  fetchInvitationByToken,
  fetchMyCompany,
} from "../api/companyWorkspace";
import {
  invitationActionableForRecipient,
  invitationStatusLabel,
  committedEnrollmentStatus,
  errorCode,
  roleLabel,
} from "../lib/companyLabels";
import { createScopeGate } from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function CompanyInvitationByTokenContent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const [invitation, setInvitation] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setBusy(false);
    }
  };

  const load = useCallback(({ background = false } = {}) => {
    if (!token) {
      setState("invalid");
      return Promise.resolve();
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({
      actorId,
      companyId: null,
      resourceId: token,
    });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
    }
    return fetchInvitationByToken(token)
      .then((data) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setInvitation(data);
        revalidationConfirmed = true;
        setActionError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return;
        }
        if (status === 404) setState("missing");
        else if (status === 403) setState("forbidden");
        else {
          setError(messageFromError(err));
          setState("error");
        }
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, scopeGate, token]);

  useEffect(() => {
    mountedRef.current = true;
    // Token is the route resource identity for this surface.
    scopeGate.bindIdentity({ actorId, companyId: token });
    busyOpRef.current = null;
    setBusy(false);
    setActionError(null);
    setInvitation(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [token, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy,
    onRevalidate: () => load({ background: true }),
  });

  const act = async (kind) => {
    if (busyOpRef.current || writesSuspendedRef.current || !token) return;
    const op = scopeGate.beginMutation({
      actorId,
      companyId: null,
      resourceId: token,
    });
    busyOpRef.current = op;
    setBusy(true);
    setActionError(null);
    try {
      if (kind === "accept") {
        await acceptInvitationByToken(token);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await fetchMyCompany();
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        navigate("/companies/mine", { replace: true });
        return;
      }
      await declineInvitationByToken(token);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const code = errorCode(err);
      const invitationStatus = committedEnrollmentStatus(err, "invitation_status");
      if (code === "already_member" || code === "membership_suspended") {
        setActionError(
          invitationStatus
            ? `Запрошення вже недійсне (${invitationStatusLabel(invitationStatus)}). Успішний вступ не відбувся.`
            : "Запрошення вже недійсне. Успішний вступ не відбувся.",
        );
      } else if (code === "company_enrollment_conflict") {
        setActionError(
          "У вас уже є поточне членство в іншій компанії. Автоматичне перемикання заборонене. Спочатку вийдіть або передайте власність.",
        );
      } else {
        setActionError(messageFromError(err));
      }
      await load();
    } finally {
      releaseBusy(op);
    }
  };

  if (state === "loading") {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження запрошення" />
      </div>
    );
  }

  if (state !== "ready") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-link-slash"
          title="Запрошення недоступне"
          description={
            error ||
            "Посилання могло закінчитися, бути відкликаним, заміненим або призначеним іншому отримувачу."
          }
          size="md"
        />
        <Link to="/companies/my-invitations" className="action-btn">
          Мої запрошення
        </Link>
      </div>
    );
  }

  const actionable = invitationActionableForRecipient(invitation);

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container feature-company-workspace__narrow">
        <CompanyPageHeader
          title="Запрошення до компанії"
          subtitle="Прийміть або відхиліть запрошення. Email-розсилка вимкнена — діє посилання та сповіщення в застосунку."
        />
        <article className="feature-company-workspace__card">
          <h2>{invitation.company?.name || "Компанія"}</h2>
          <p>
            Роль: {roleLabel(invitation.role)} · статус:{" "}
            {invitationStatusLabel(invitation.status)}
          </p>
          {invitation.company?.status === "archived" ? (
            <p className="feature-company-workspace__note">
              Компанія в архіві — прийняття запрошення недоступне.
            </p>
          ) : null}
          {actionError ? <InlineError>{actionError}</InlineError> : null}
          {actionable ? (
            <div className="feature-company-workspace__inline-actions">
              <button
                type="button"
                className="action-btn"
                disabled={busy}
                onClick={() => act("accept")}
              >
                Прийняти
              </button>
              <button
                type="button"
                className="action-btn action-btn--danger"
                disabled={busy}
                onClick={() => act("decline")}
              >
                Відхилити
              </button>
            </div>
          ) : (
            <EmptyState
              icon="fa-solid fa-circle-info"
              title="Запрошення недоступне для дії"
              description={`Поточний статус: ${invitationStatusLabel(invitation.status)}.`}
              size="sm"
            />
          )}
        </article>
      </div>
    </section>
  );
}

export default CompanyInvitationByTokenContent;
